import type { EditorView } from 'prosemirror-view';
import type { WebEditorComment } from '../comments.js';

/** Private review UI owned and disposed by the ProseMirror plugin view. */
export const createCommentPinView = (initialView: EditorView, options: {
  comments: (view: EditorView) => readonly WebEditorComment[];
  position: (view: EditorView, id: string) => number | undefined;
  select: (view: EditorView, comment: WebEditorComment) => void;
  move?: (comment: WebEditorComment, anchor: WebEditorComment['anchor']) => void;
}) => {
  let view = initialView;
  const document = view.dom.ownerDocument;
  const layer = document.createElement('div');
  layer.className = 'we-comment-pins'; layer.contentEditable = 'false';
  Object.assign(layer.style, { position: 'fixed', inset: '0', pointerEvents: 'none', zIndex: '100' });
  document.body.append(layer);
  let scheduled = 0;
  let dragCleanup: (() => void) | undefined;
  const render = () => {
    scheduled = 0;
    if (dragCleanup) return;
    const focusedId = layer.contains(document.activeElement) ? (document.activeElement as HTMLElement).dataset.commentId : undefined;
    layer.replaceChildren();
    for (const comment of options.comments(view)) {
      const pos = options.position(view, comment.anchor.elementId); if (pos === undefined || comment.status !== 'open') continue;
      const target = view.nodeDOM(pos); if (!target || target.nodeType !== 1) continue;
      const rect = (target as HTMLElement).getBoundingClientRect();
      const button = document.createElement('button'); button.type = 'button'; button.className = 'we-comment-pin';
      button.dataset.commentId = comment.id;
      button.setAttribute('aria-label', `Kommentar: ${comment.text}`); button.textContent = '●';
      Object.assign(button.style, { position: 'absolute', left: `${rect.left + comment.anchor.x * rect.width}px`, top: `${rect.top + comment.anchor.y * rect.height}px`, transform: 'translate(-50%, -50%)', pointerEvents: 'auto' });
      button.addEventListener('click', () => options.select(view, comment));
      if (options.move) button.addEventListener('pointerdown', event => {
        if (event.button !== 0) return;
        const origin = { x: event.clientX, y: event.clientY }; let next = comment.anchor; let moved = false;
        button.setPointerCapture(event.pointerId);
        const move = (pointer: PointerEvent) => {
          moved ||= Math.hypot(pointer.clientX - origin.x, pointer.clientY - origin.y) > 4;
          if (!moved) return;
          next = { ...comment.anchor, x: Math.max(0, Math.min(1, (pointer.clientX - rect.left) / Math.max(1, rect.width))), y: Math.max(0, Math.min(1, (pointer.clientY - rect.top) / Math.max(1, rect.height))) };
          button.style.left = `${rect.left + next.x * rect.width}px`; button.style.top = `${rect.top + next.y * rect.height}px`;
        };
        const finish = (commit: boolean) => {
          dragCleanup?.(); dragCleanup = undefined;
          if (commit && moved) options.move?.(comment, next);
          render();
        };
        const up = () => finish(true); const cancel = () => finish(false);
        const key = (keyboard: KeyboardEvent) => { if (keyboard.key === 'Escape') { keyboard.preventDefault(); cancel(); } };
        button.addEventListener('pointermove', move); button.addEventListener('pointerup', up); button.addEventListener('pointercancel', cancel); document.addEventListener('keydown', key);
        dragCleanup = () => { button.removeEventListener('pointermove', move); button.removeEventListener('pointerup', up); button.removeEventListener('pointercancel', cancel); document.removeEventListener('keydown', key); };
      });
      if (options.move) button.addEventListener('keydown', event => {
        const delta = { ArrowLeft: [-.01, 0], ArrowRight: [.01, 0], ArrowUp: [0, -.01], ArrowDown: [0, .01] }[event.key];
        if (!delta) return; event.preventDefault();
        options.move?.(comment, { ...comment.anchor, x: Math.max(0, Math.min(1, comment.anchor.x + delta[0]!)), y: Math.max(0, Math.min(1, comment.anchor.y + delta[1]!)) });
      });
      layer.append(button);
      if (focusedId === comment.id) button.focus({ preventScroll: true });
    }
  };
  const schedule = () => { if (!scheduled) scheduled = document.defaultView!.requestAnimationFrame(render); };
  const observer = new ResizeObserver(schedule); observer.observe(view.dom);
  document.defaultView?.addEventListener('resize', schedule); document.addEventListener('scroll', schedule, true); render();
  return {
    update: (next: EditorView) => { view = next; schedule(); },
    destroy: () => { dragCleanup?.(); observer.disconnect(); if (scheduled) document.defaultView?.cancelAnimationFrame(scheduled); document.defaultView?.removeEventListener('resize', schedule); document.removeEventListener('scroll', schedule, true); layer.remove(); },
  };
};
